/**
 * What MAHI actually says on a first landing, and where she stands to say it.
 *
 * Content lives apart from the mechanics (see DiscoveryTour.jsx) because these
 * are two different kinds of change: the copy here is a voice decision that
 * will get revised often, the choreography there is not.
 *
 * `target` is a `data-tour` attribute value, resolved at run time rather than
 * held as a ref. The try-on button lives three components deep inside the card
 * — and only exists while a card is on top — so threading a ref out to the feed
 * would mean prop-drilling through ProductSwipeCard for something that is
 * purely presentational. A `data-tour` hook is also self-describing at the call
 * site, unlike reusing `data-testid`, which would quietly couple this to the
 * test suite.
 *
 * Five steps, deliberately. The wardrobe earns its place because a saved dress
 * that seems to vanish is the one thing here that reads as broken rather than
 * unexplored. Everything else on the screen — Buy Now, undo, tapping a photo
 * to open it full-screen — is discoverable by looking at it, and each extra
 * spotlight before someone has seen a single dress is another chance to lose
 * them.
 */

/** First name only, and only if it looks like one — never "Hi undefined". */
export function firstNameOf(user) {
  const raw = (user?.name || '').trim();
  if (!raw) return '';
  const first = raw.split(/\s+/)[0];
  return first.length > 1 && first.length <= 20 ? first : '';
}

export function buildTourSteps(user) {
  const name = firstNameOf(user);

  return [
    {
      id: 'welcome',
      // No target: she takes the middle of the screen for the greeting, then
      // starts moving. Arriving already attached to a button would skip the
      // beat where you realise she is going to show you around.
      target: null,
      title: name ? `Hi ${name}` : 'Hi there',
      body: 'I’m MAHI, your AI stylist. Fifteen seconds and you’ll know your way around.',
      mood: 'happy',
    },
    {
      id: 'swipe',
      target: 'card',
      title: 'Swipe to tell me what you like',
      body: 'Right to keep it, left to pass. I learn your taste as you go - the more you swipe, the better I get.',
      mood: 'happy',
    },
    {
      id: 'tryon',
      target: 'tryon',
      title: 'See it on you',
      body: 'Tap here and I’ll put it on you - actually you, not a model. Takes about fifteen seconds.',
      mood: 'celebrating',
    },
    {
      id: 'wardrobe',
      target: 'wardrobe',
      title: 'Everything you keep lands here',
      body: 'Your wardrobe. Come back any time to look again, compare two, or buy.',
      mood: 'happy',
    },
    {
      id: 'ask',
      target: 'orb',
      title: 'Or just ask me',
      body: '“Something for a sangeet under ₹5,000.” Tap me here and I’ll go find it.',
      mood: 'thinking',
    },
  ];
}
